import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|heic|svg)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|mkv|avi|mov|wmv|flv)$/i;

const MIME_LABEL = {
  "application/pdf": "PDF",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "text/plain": "TXT",
  "text/csv": "CSV",
  "application/zip": "ZIP",
};

function getTypeLabel(file) {
  if (MIME_LABEL[file.type]) return MIME_LABEL[file.type];
  const ext = file.name.split(".").pop();
  return ext ? ext.toUpperCase().slice(0, 8) : "FILE";
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

let _uid = 0;
const uid = () => `uf-${++_uid}`;

const UploadFile = forwardRef(({ modifier, multiple = true, accept = "*/*", noTrigger = false, children }, ref) => {
  const [items, setItems] = useState([]);
  const inputRef = useRef();

  useImperativeHandle(ref, () => ({ open: () => inputRef.current?.click() }));

  const notify = useCallback((next) => {
    setItems(next);
    if (typeof modifier === "function") modifier(next.map((i) => i.file));
  }, [modifier]);

  const handleChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const incoming = selected.map((file) => {
      const isImage = file.type.startsWith("image/") || IMAGE_EXT.test(file.name);
      const isVideo = file.type.startsWith("video/") || VIDEO_EXT.test(file.name);
      return { key: uid(), file, src: null, isImage, isVideo };
    });

    incoming.forEach((entry) => {
      if (!entry.isImage) return;
      const reader = new FileReader();
      reader.readAsDataURL(entry.file);
      reader.onload = (ev) => {
        setItems((prev) => {
          const next = prev.map((i) =>
            i.key === entry.key ? { ...i, src: ev.target.result } : i
          );
          if (typeof modifier === "function") modifier(next.map((i) => i.file));
          return next;
        });
      };
    });

    notify([...items, ...incoming]);
    e.target.value = "";
  };

  const remove = (key) => {
    notify((prev) => prev.filter((i) => i.key !== key));
  };

  const renderPreview = () => {
    if (!items.length) return null;
    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        {items.map((item) => {
          if (item.isImage) {
            return (
              <div
                key={item.key}
                className="position-relative rounded overflow-hidden"
                style={{ width: 80, height: 80, flexShrink: 0 }}
              >
                {item.src ? (
                  <img
                    src={item.src}
                    alt={item.file.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center bg-light w-100 h-100"
                  >
                    <div className="spinner-border spinner-border-sm text-secondary" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => remove(item.key)}
                  className="position-absolute top-0 end-0 btn btn-sm p-0 d-flex align-items-center justify-content-center"
                  style={{
                    width: 20,
                    height: 20,
                    background: "rgba(0,0,0,0.55)",
                    borderRadius: "50%",
                    border: "none",
                    margin: 3,
                    lineHeight: 1,
                  }}
                >
                  <i className="fa fa-times text-white" style={{ fontSize: 9 }} />
                </button>
              </div>
            );
          }

          return (
            <div
              key={item.key}
              className="d-flex align-items-center gap-2 rounded px-2 py-1 position-relative"
              style={{
                background: "rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.12)",
                maxWidth: 200,
                minWidth: 0,
              }}
            >
              <i className="fa fa-file text-secondary" style={{ fontSize: 20, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div
                  className="small fw-semibold text-truncate"
                  style={{ maxWidth: 120 }}
                  title={item.file.name}
                >
                  {item.file.name}
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {getTypeLabel(item.file)} · {formatSize(item.file.size)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(item.key)}
                className="btn btn-sm p-0 ms-1 d-flex align-items-center justify-content-center"
                style={{
                  width: 18,
                  height: 18,
                  background: "rgba(0,0,0,0.15)",
                  borderRadius: "50%",
                  border: "none",
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-times" style={{ fontSize: 9 }} />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {renderPreview()}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {!noTrigger && (
        <div onClick={() => inputRef.current?.click()} style={{ cursor: "pointer" }}>
          {children ?? <i className="fa fa-paperclip" />}
        </div>
      )}
    </>
  );
});

export default UploadFile;
